
import {
  defineComponent,
  ref,
  shallowRef,
  onMounted,
  computed,
  nextTick,
  watch,
  provide,
} from "vue";
import ActivityModal from "@/views/user/modal/ActivityModal.vue";
import { SiteApi } from "@/services/api/Site.service";
import { ISite } from "@/interfaces/iSite";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import VZSideDrawer from "@/components/content/VZSideDrawer.vue";
import {
  FormatMethods,
  getMaxLengthErrorMessage,
  getTimeZoneList,
} from "@/helpers/data-format";
import { usePagination } from "@/helpers/pagination";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { IBulkDelete } from "@/interfaces/iPageModel";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { TranslationService } from "@/core/services/TranslationService";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { DrawerComponent } from "@/assets/ts/components";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import { useToast } from "vue-toastification";
import SiteCloneModal from "@/components/modals/forms/SiteCloneModal.vue";
import { getLookups } from "@/services/api/Lookup.service";
import {
  siteTableBulkDelete,
  siteTableFilters,
  siteTableHeaders,
  SITE_TABS,
} from "./siteDataTableOptions";
import * as Yup from "yup";
import { hasPermission } from "@/core/services/permission.service";
import { Modal } from "bootstrap";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";
import UserSite from "@/store/userSite";
import { FETCH_ALL_SITES, SITE_STORE } from "@/store/userSite/types";
import { useClipboard } from "@vueuse/core";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
declare global {
  interface Window {
    calltheFunction: (event) => void;
  }
}
export default defineComponent({
  name: "site-index",
  // eslint-disable-next-line vue/no-unused-components
  components: {
    Datatable,
    VZSideDrawer,
    VZDeleteModal,
    ActivityModal,
    "vz-site-clone-modal": SiteCloneModal,
  },
  setup() {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const store = useStore();
    getModule(UserSite);
    const datatable = ref<null | HTMLFormElement>(null);
    const addMessage = t("pages.site.siteAddedMessage");
    const editMessage = t("pages.site.siteUpdatedMessage");
    const updateDefaultUserMessage = t(
      "pages.site.siteUpdatedMessageWithDefaultUser"
    );
    const addDefaultUserMessage = t(
      "pages.site.siteAddedMessageWithDefaultUser"
    );
    const showDeleteDialog = ref<any>(false);
    const { page, total, rowsPerPage } = usePagination();
    const loading = ref(false);
    const openCloneModal = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showSearch = true;
    const sideDrawerTitle = ref<string>("");
    const sideDrawerIcon = ref<string>("");
    const siteWebId = ref<any>();
    const openDrawer = ref<boolean>(false);
    const options = ref<{ label: string; value: string }[]>([]);
    const tableData = ref<ISite[]>([]);
    const selectedForAction = ref<Partial<ISite>>({});
    const bulkDelete = ref<IBulkDelete>(siteTableBulkDelete());
    const tableHeader = ref(siteTableHeaders());
    const tableFilter = ref(siteTableFilters());
    const tabs = shallowRef<ITabConfig[]>(SITE_TABS());
    const cityOptions = ref<{ label: string; value: string }[]>([]);
    const stateOptions = ref<{ label: string; value: string }[]>([]);
    const postalCodeOptions = ref<{ label: string; value: string }[]>([]);
    const city = ref<any>([]);
    const postalCode = ref<any>([]);
    const state = ref<any>([]);
    const stateNames = ref<any>([]);
    const cityNames = ref<any>([]);
    const postalCodeNames = ref<any>([]);
    let siteObj = ref<any>({});
    const clipboardCopyNotSupported = t(
      "pages.event.clipboardCopyNotSupported"
    );
    const copyButtonRef = ref<null | HTMLElement>(null);
    const source = ref("");
    const currentUser = computed(() => {
      return store.getters.currentUser;
    });
    const { copy, isSupported } = useClipboard({
      source,
    });
    const timeZoneOptions = computed(() => getTimeZoneList());
    let siteFilterWithDropdowns = ref([
      {
        key: "extra",
        label: TranslationService.translate("extra"),
        type: "autocomplete",
        textKey: "extra",
        valueKey: "value",
        attrs: {
          mode: "tags",
          class: "d-none",
        },
        options: cityOptions.value,
      },
      {
        key: "state",
        label: TranslationService.translate("State"),
        type: "autocomplete",
        textKey: "state",
        valueKey: "value",
        attrs: {
          mode: "tags",
        },
        options: stateOptions.value,
        onChange: async (value) => {
          if (datatable.value?.VZFilter?.models) {
            datatable.value.VZFilter.models["city"] = [];
            datatable.value.VZFilter.models["postalCode"] = [];
            datatable.value.VZFilter.models["filterSiteIds"] = [];
          }

          state.value = value;
          city.value = [];
          postalCode.value = [];
          cityNames.value = [];
          postalCodeNames.value = [];

          // Get state names from selected values
          stateNames.value = stateOptions.value
            .filter((item) => value.includes(item.value))
            .map((item) => item.label);

          await getLookupCities(value);
          await getLookupPostalCodes();
          loadSites();
        },
      },
      {
        key: "city",
        label: TranslationService.translate("City"),
        type: "autocomplete",
        textKey: "city",
        valueKey: "value",
        attrs: {
          mode: "tags",
        },
        options: cityOptions.value,
        onChange: async (value) => {
          if (datatable.value?.VZFilter?.models) {
            datatable.value.VZFilter.models["postalCode"] = [];
            datatable.value.VZFilter.models["filterSiteIds"] = [];
          }

          city.value = value;
          postalCode.value = [];
          postalCodeNames.value = [];

          // Get city names from selected values
          cityNames.value = cityOptions.value
            .filter((item) => value.includes(item.value))
            .map((item) => item.label);

          await getLookupPostalCodes();
          loadSites();
        },
      },
      {
        key: "postalCode",
        label: TranslationService.translate("Postal Code"),
        type: "autocomplete",
        textKey: "postalCode",
        valueKey: "value",
        attrs: {
          mode: "tags",
        },
        options: postalCodeOptions.value,
        onChange: (value) => {
          if (datatable.value?.VZFilter?.models) {
            datatable.value.VZFilter.models["filterSiteIds"] = [];
          }

          postalCode.value = value;

          // Get postal code names from selected values
          postalCodeNames.value = postalCodeOptions.value
            .filter((item) => value.includes(item.value))
            .map((item) => item.label);

          loadSites();
        },
      },
      {
        key: "filterSiteIds",
        label: TranslationService.translate("common.sites"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: `site/sites?status=ACTIVE&size=10000&city=${
          city.value && city.value.length > 0 ? city.value[0] : ""
        }&state=${
          stateNames.value && stateNames.value.length > 0
            ? stateNames.value
            : ""
        }&postalCode=${postalCode.value}`,
        textKey: "content.name",
        valueKey: "content.webId",
        payload: city.value,
      },
      {
        key: "status",
        label: TranslationService.translate("common.status"),
        type: "select",
        options: [
          { label: TranslationService.translate("common.all"), value: "" },
          {
            label: TranslationService.translate("common.active"),
            value: "ACTIVE",
          },
          {
            label: TranslationService.translate("common.inactive"),
            value: "INACTIVE",
          },
        ],
      },
    ]);

    const getLookupState = async () => {
      stateOptions.value = [];
      let res;

      res = await getLookups(`site/filter/SITE_STATES`);

      stateOptions.value = res.map((data) => ({
        label: data.text,
        value: data.value,
      }));
      let selectAll = { label: "Select All", value: "-1" };
      stateOptions.value.unshift(selectAll);

      siteFilterWithDropdowns.value[1].options = stateOptions.value;
    };

    const getLookupCities = async (id) => {
      cityOptions.value = [];
      let res;
      if (id && id.length > 0) {
        res = await getLookups(`site/filter/SITE_CITIES?siteIds=${id}`);
      } else {
        res = await getLookups(`site/filter/SITE_CITIES?userId=123456`);
      }
      cityOptions.value = res.map((data) => ({
        label: data.text,
        value: data.value,
      }));
      let selectAll = { label: "Select All", value: "-1" };
      cityOptions.value.unshift(selectAll);
      siteFilterWithDropdowns.value[2].options = cityOptions.value;
    };

    const getLookupPostalCodes = async () => {
      postalCodeOptions.value = [];
      var res = [] as any;
      if (city.value.length > 0 || state.value.length > 0) {
        res = await getLookups(
          `site/filter/SITE_POSTAL_CODES?siteIds=${
            city.value.length > 0 ? city.value : state.value
          }`
        );
      } else {
        res = await getLookups(`site/filter/SITE_POSTAL_CODES?userId=123456`);
      }

      postalCodeOptions.value = res.map((data) => ({
        label: data.text,
        value: data.value,
      }));
      let selectAll = { label: "Select All", value: "-1" };
      postalCodeOptions.value.unshift(selectAll);

      siteFilterWithDropdowns.value[3].options = postalCodeOptions.value;
    };

    const loadSites = () => {
      const updatedUrl = [...siteFilterWithDropdowns.value];

      // Build query params properly
      const cityParam =
        cityNames.value.length > 0 ? cityNames.value.join(",") : "";
      const stateParam =
        stateNames.value.length > 0 ? stateNames.value.join(",") : "";
      const postalCodeParam =
        postalCodeNames.value.length > 0 ? postalCodeNames.value.join(",") : "";

      updatedUrl[4]["url"] = `site/sites?status=ACTIVE&size=10000${
        cityParam ? `&city=${encodeURIComponent(cityParam)}` : ""
      }${stateParam ? `&state=${encodeURIComponent(stateParam)}` : ""}${
        postalCodeParam
          ? `&postalCode=${encodeURIComponent(postalCodeParam)}`
          : ""
      }`;

      siteFilterWithDropdowns.value = [...updatedUrl];
    };

    let permissionModel = ref<any>({
      create: "VYZOR:BUSINESS:SITE:CREATE",
      delete: "VYZOR:BUSINESS:SITE:DELETE",
      update: "VYZOR:BUSINESS:SITE:UPDATE",
    });

    let formData = ref<IAddEditFormModel>({
      page: "site",
      header: "Site",
      edit: false,
      data: {},
      defaultValues: {
        status: "Active",
      },
      label: [
        { addHeader: t("pages.site.addSite") },
        { editHeader: t("pages.site.editSite") },
      ],
      validationSchema: Yup.object({
        name: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.name") + " " + t("common.isRequired")),
        code: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("common.number"), 30))
          .required(t("common.number") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.number") + " " + t("common.isRequired")),
        startDate: Yup.date()
          .transform((curr, orig) => (orig === "" ? null : curr))
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .transform((curr, orig) => (orig === "" ? null : curr))
          .min(Yup.ref("startDate"), t("validation.greaterEndDate"))
          .nullable(),
        address: Yup.string()
          .strict(true)
          .max(300, getMaxLengthErrorMessage(t("common.address"), 300))
          .required(t("common.address") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.address") + " " + t("common.isRequired")),
        storeEmail: Yup.string()
          .email()
          .matches(
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
            t("validation.emailValidation")
          )
          .max(100, getMaxLengthErrorMessage(t("common.email"), 100))
          .required(t("common.email") + " " + t("common.isRequired")),
        website: Yup.string()
          .max(100, getMaxLengthErrorMessage(t("pages.site.website"), 100))
          .nullable(),
        timezone: Yup.string()
          .required(t("common.timezone") + " " + t("common.isRequired"))
          .nullable(),
        operationHours: Yup.object().nullable(), // Add this line
        status: Yup.string()
          .strict(true)
          .required(t("common.status") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.status") + " " + t("common.isRequired")),
        branchType: Yup.string().required(
          t("common.siteLevel") + " " + t("common.isRequired")
        ),
        userEmail: Yup.string()
          .email()
          .matches(
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
            t("validation.emailValidation")
          ),
        description: Yup.string()
          .max(500, getMaxLengthErrorMessage(t("common.description"), 500))
          .nullable(),
      }),
      model: [
        {
          name: "name",
          label: t("common.name"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "code",
          label: t("common.number"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "branchType",
          label: t("common.siteLevel"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch level",
          options: options.value,
          multiSelect: false,
        },

        {
          name: "storeEmail",
          label: t("common.email"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          fieldType: "email",
          placeholder: "Email@example.com",
        },
        {
          name: "website",
          label: t("pages.site.website"),
          type: "text",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "address",
          label: t("common.address"),
          type: "address",
          required: true,
          showOnlyInEdit: false,
          disabled: true,
          placeholder: "Address Line 1, City, State, Zip, Country",
        },
        {
          name: "timezone",
          label: "Time Zone",
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select time zone",
          options: timeZoneOptions.value,
          multiSelect: false,
        },
        {
          name: "operationHours",
          label: t("pages.site.operationalHours"),
          type: "operationalHours",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: t("pages.site.optional"),
        },
        {
          name: "startDate",
          label: t("common.startDate"),
          type: "date",
          required: true,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "endDate",
          label: t("common.endDate"),
          type: "date",
          required: false,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "status",
          label: t("common.status"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: true,
          placeholder: "Please select status",
          options: [
            {
              label: t("common.active"),
              value: "Active",
            },
            {
              label: t("common.inactive"),
              value: "Inactive",
            },
          ],
        },
        {
          name: "description",
          label: t("common.description"),
          type: "textarea",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "defaultUser",
          label: t("common.defaultUser"),
          type: "checkbox2",
          required: false,
          showOnlyInEdit: false,
          disabled: false,
          PermssionChecked: currentUser.value,
        },
        {
          name: "userEmail",
          label: t("common.userEmail"),
          type: "text2",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          fieldType: "email",
          placeholder: "Email@example.com",
        },
      ],
    });
    const setSiteCloneData = async (data: any) => {
      let existingElement = document.querySelector(
        ".site-clone.show-clone-" + data.webId
      );
      if (existingElement) {
        existingElement.classList.remove("show");
      }
      openCloneModal.value = true;
      siteObj.value = data;
      // await nextTick();
      // const modal = new Modal(
      //   document.getElementById(`kt_site_clone`) as HTMLElement
      // );
      // modal.show();
    };
    const localPreference = ref({});
    const format = computed(() => {
      return FormatMethods;
    });
    const tableRowClick = (item) => {
      openDrawer.value = true;
      tabs.value = tabs.value.map((tab) => {
        return {
          ...tab,
          props: { ...tab.props, data: item, openDrawer: openDrawer },
        };
      });
      sideDrawerTitle.value = item.name;
      sideDrawerIcon.value = "bi bi-diagram-3";
      DrawerComponent?.getInstance("vz__drawer")?.toggle();
    };
    const resetWebId = (id) => {
      if (siteWebId.value === undefined || siteWebId.value === id) {
        siteWebId.value = id;
      } else if (siteWebId.value !== undefined) {
        let existingElement = document.querySelector(
          ".site-clone.show-clone-" + siteWebId.value
        );
        if (existingElement) {
          existingElement.classList.remove("show");
        }
        siteWebId.value = id;
      }
    };
    onMounted(async () => {
      window.calltheFunction = (event: MouseEvent) => {
        event.stopPropagation(); // Prevent the parent click event from firing
        copyText();
        const icon = document.getElementById("copyIcon");
        if (icon) {
          icon.classList.remove("fa-copy"); // Remove the copy icon class
          icon.classList.add("fa-check"); // Add the trash icon class
          const colorCopy = icon.style.color;
          icon.style.color = "#00ff7f";
          icon.innerText = "Copied";
          setTimeout(() => {
            icon.classList.add("fa-copy"); // Remove the copy icon class
            icon.classList.remove("fa-check"); // Add the trash icon class
            icon.style.color = colorCopy;
            icon.innerText = "";
          }, 1000);
        }
      };

      setCurrentPageBreadcrumbs("Sites", ["Home", "Sites"]);
      expandMenu();
      loading.value = true;
      var drawerElement = document.getElementById("vz__drawer");
      drawerElement?.addEventListener("transitionend", function (event) {
        if (event?.propertyName === "transform") {
          const targetElement = event.target as HTMLElement;
          if (targetElement.className === "drawer drawer-end") {
            // refreshList();
            openDrawer.value = false;
          }
        }
      });
      getLookupBranch();
      getLookupState();
      getLookupCities([]);
      getLookupPostalCodes();

      // queryUpdate({
      //   ...defaultFilter.value,
      //   page: page.value - 1,
      //   size: rowsPerPage.value,
      // });

      const closeFilterDialogBtn = document.getElementById(
        "closeFilterDialogBtn" + "sites"
      );
      if (closeFilterDialogBtn) {
        closeFilterDialogBtn.addEventListener("click", function () {
          city.value = [];
          state.value = [];
          postalCode.value = [];
          stateNames.value = [];
          cityNames.value = [];
          postalCodeNames.value = [];
          getLookupCities([]);
          getLookupPostalCodes();
          loadSites();
        });
      } else {
        // console.error("Button not found!");
      }
    });
    watch(
      showDeleteDialog,
      (showDeleteDialog) => {
        if (showDeleteDialog) {
          let existingElement = document.querySelector(
            ".site-clone.show-clone-" + siteWebId.value
          );
          if (existingElement) {
            existingElement.classList.remove("show");
          }
        }
      },
      { deep: true }
    );
    const queryUpdate = async (params?: any) => {
      try {
        if (
          params &&
          !params.filterSiteIds &&
          !params.state &&
          !params.city &&
          !params.postalCode
        ) {
          state.value = [];
          city.value = [];
          postalCode.value = [];
          stateNames.value = [];
          cityNames.value = [];
          postalCodeNames.value = [];
          getLookupCities([]);
          getLookupPostalCodes();
          loadSites();
        }
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        localPreference.value = { ...params };
        if (params && params.state) {
          const result = stateOptions.value
            .filter((item) =>
              params.state.some((value) => value === item.value)
            )
            .map((item) => item.label);

          params.state = result;
        }
        if (params && params.city) {
          const result = cityOptions.value
            .filter((item) => params.city.some((value) => value === item.value))
            .map((item) => item.label);

          params.city = result;
        }
        if (params && params.postalCode) {
          const result = postalCodeOptions.value
            .filter((item) =>
              params.postalCode.some((value) => value === item.value)
            )
            .map((item) => item.label);

          params.postalCode = result;
        }

        const data = await SiteApi.getInstance().getSiteList({
          ...params,
        });
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
        getLookupCities([]);
        getLookupPostalCodes();
      } finally {
        loading.value = false;
      }
    };
    const getLookupBranch = async () => {
      await getLookups(
        "static-data/filter/LOOKUP_FILTER/filters?filter[lookupName]=LKBranchLevel"
      )
        .then((res) => {
          res.map((data) => {
            options.value.push({
              label: data.text,
              value: data.value,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });
      formData.value.model[2].options = options.value;
    };
    const editSite = (item: any) => {
      let existingElement = document.querySelector(
        ".site-clone.show-clone-" + siteWebId.value
      );
      if (existingElement) {
        existingElement.classList.remove("show");
      }

      if (datatable.value) {
        datatable.value.openAddEditModal = true;
      }
      const updatedItem = { ...item, branchType: item.branchType.toString() };
      formData.value.data = updatedItem;
      formData.value.edit = true;
    };
    const submit = async (reqData) => {
      let message = "";
      if (
        currentUser?.value &&
        currentUser?.value?.userRoleModels[0] &&
        (currentUser?.value?.userRoleModels[0]?.name === "Head Quarter Staff" ||
          currentUser?.value?.userRoleModels[0]?.name === "Client Admin")
      ) {
        // Check if defaultUser is undefined and set it to true
        if (reqData.defaultUser === undefined) {
          reqData.defaultUser = "true";
        }

        // Check if defaultUser is "on" and set it to "false"
        if (reqData.defaultUser === "on") {
          reqData.defaultUser = "false";
        } else {
          reqData.defaultUser = "true";
        }
        message = formData.value.edit
          ? updateDefaultUserMessage
          : addDefaultUserMessage;
      } else {
        message = formData.value.edit ? editMessage : addMessage;
      }

      if (reqData.address) {
        delete reqData.address;
      }
      if (reqData.status) {
        delete reqData.status;
      }

      //loading.value = true;
      reqData = formData.value.edit ? reqData : { ...reqData };

      await SiteApi.getInstance()
        .addSite(reqData, formData.value.edit)
        .then(async (e) => {
          if (datatable.value && datatable.value.openAddEditModal) {
            datatable.value.openAddEditModal = false;
          }
          toast.success(message);
          formData.value.edit = false;
          queryUpdate({
            ...localPreference.value,
            page: page.value - 1,
            size: rowsPerPage.value,
          });

          datatable.value?.VZFilter.value?.getLookupData();
          await store.dispatch(`${SITE_STORE}/${FETCH_ALL_SITES}`);
        })
        .catch(() => {
          //
        })
        .finally(() => {
          // loading.value = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate({ ...localPreference.value });
    };
    const loadSiteStore = async () => {
      // await store.dispatch(`${SITE_STORE}/${FETCH_ALL_SITES}`);
      refreshList();
    };
    const expandMenu = () => {
      let menuItems = document.querySelectorAll(".side-menu-class");
      menuItems[1].className =
        "menu-link accordion-button border-0 bg-transparent py-4 shadow-none text-hover-white side-menu-class";
      let childItems = document.querySelectorAll(
        ".site-menu-item-class, .collapse"
      );
      childItems.forEach((element) => {
        if (element.id == "menu-section-1") {
          element.className =
            "accordion-collapse menu-item site-menu-item-class collapse show";
        }
      });
    };
    const copyText = () => {
      if (isSupported) {
        const textTooltip = document.getElementById("tootip-element");
        if (textTooltip && textTooltip.innerText) {
          copy(textTooltip.innerText);
        }
      } else {
        toast.error(clipboardCopyNotSupported);
      }
    };
    const closeDrawer = () => {
      openDrawer.value = false;
      DrawerComponent?.getInstance("vz__drawer")?.hide();
    };
    provide("refreshSiteList", async () => {
      await queryUpdate({
        ...localPreference.value,
        page: page.value - 1,
        size: rowsPerPage.value,
      });
    });
    provide("closeDrawer", () => {
      closeDrawer();
    });

    const handleQueryUpdate = async (query) => {
      await queryUpdate({ ...query });
      store.dispatch("updateUser");
    };
    return {
      timeZoneOptions,
      bulkDelete,
      format,
      formData,
      tabs,
      tableData,
      tableHeader,
      tableFilter,
      sideDrawerTitle,
      sideDrawerIcon,
      showSearch,
      openDrawer,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      editSite,
      tableRowClick,
      queryUpdate,
      submit,
      t,
      permissionModel,
      hasPermission,
      refreshList,
      siteObj,
      setSiteCloneData,
      expandMenu,
      openCloneModal,
      loadSiteStore,
      datatable,
      showDeleteDialog,
      resetWebId,
      copyText,
      copy,
      source,
      copyButtonRef,
      options,
      siteFilterWithDropdowns,
      cityOptions,
      getLookupCities,
      stateOptions,
      getLookupPostalCodes,
      city,
      state,
      postalCode,
      stateNames,
      loadSites,
      postalCodeNames,
      cityNames,
      currentUser,
      closeDrawer,
      handleQueryUpdate,
    };
  },
});
